import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { isMatch, isValid, parse } from 'date-fns';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { Rarity, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { withFragments } from '@sorare/core/src/lib/gql';

import bundesligaPlayers from '#src/assets/new-season/bundesliga_players.webp';
import laligaPlayers from '#src/assets/new-season/laliga_players.webp';
import ligue1Players from '#src/assets/new-season/ligue_1_players.webp';
import premierLeaguePlayers from '#src/assets/new-season/premier_league_players.webp';
import { useMarketHomeFilters } from '#src/hooks/useMarketHomeFilters.ts';

import type {
  getLeagueAvatar_competition,
  getLeagueAvatar_so5Competition,
  getLeagueBackgroundUrl_competition,
  getLeagueBackgroundUrl_so5Competition,
} from './__generated__/newSeason.graphql';

type LaunchType = 'MAIN_LEAGUE' | 'SECONDARY_LEAGUE';

type BaseRewardConfig = {
  usdAmount?: number;
  baseUsdAmount?: number;
  boost?: number;
};

export type ProHotStreakConfig = BaseRewardConfig & {
  levelNumber?: number;
};

export type ArenaPvpConfig = BaseRewardConfig & {
  startGameweek?: number;
  endGameweek?: number;
};

type LeagueLaunchRewards = {
  proHotStreak?: Partial<Record<Rarity, ProHotStreakConfig>>;
  arenaPvp?: Partial<Record<Rarity, ArenaPvpConfig>>;
};

export type LeagueLaunch<T = undefined, C = unknown> = {
  slug: string;
  name: string;
  startDate: Date;
  competition?: T;
  launchType: LaunchType;
  playersUrl?: string;
  so5Competition?: C;
  canBeFeatured: boolean;
  featured?: boolean;
  sport: Sport;
  rewards?: LeagueLaunchRewards;
  iconNode?: ReactNode;
  smallIconNode?: ReactNode;
  backgroundUrl?: string;
  extraFilters?: string;
  clubs?: {
    __typename: 'Club';
    slug: string;
    name: string;
    avatarUrl: string;
  }[];
};

type LeagueLaunchConfig = {
  slug: string;
  name: string;
  // `YYYYMMDD-HH:mm`, interpreted as UTC so the countdown is tz-independent.
  startDate: string;
  launchType: LaunchType;
  canBeFeatured: boolean;
  // Manual "Featured" tag: keep this (released) league promoted at the top.
  featured?: boolean;
  rewards?: LeagueLaunchRewards;
  backgroundUrl?: string;
};

const LEAGUE_PLAYERS_URLS: Record<string, string> = {
  'premier-league-gb-eng': premierLeaguePlayers,
  'ligue-1-fr': ligue1Players,
  'laliga-es': laligaPlayers,
  'bundesliga-de': bundesligaPlayers,
};

const LAUNCH_DATE_FORMAT = 'yyyyMMdd-HH:mm';

const parseLaunchDate = (value: string): Date | null => {
  const trimmed = value.trim();
  if (!isMatch(trimmed, LAUNCH_DATE_FORMAT)) return null;

  const parsed = parse(trimmed, LAUNCH_DATE_FORMAT, new Date(0));
  if (!isValid(parsed)) return null;

  return new Date(
    Date.UTC(
      parsed.getFullYear(),
      parsed.getMonth(),
      parsed.getDate(),
      parsed.getHours(),
      parsed.getMinutes()
    )
  );
};

export const useLeagueLaunches = (): LeagueLaunch<undefined>[] => {
  const {
    flags: { footballLeagueLaunches2027 = [] },
  }: { flags: { footballLeagueLaunches2027?: LeagueLaunchConfig[] } } =
    useFeatureFlags();

  return useMemo(
    () =>
      footballLeagueLaunches2027.flatMap(config => {
        const startDate = parseLaunchDate(config.startDate);
        if (!startDate) return [];
        return [
          {
            slug: config.slug,
            name: config.name,
            startDate,
            launchType: config.launchType,
            canBeFeatured: config.canBeFeatured,
            featured: config.featured,
            sport: Sport.FOOTBALL,
            rewards: config.rewards,
            playersUrl: LEAGUE_PLAYERS_URLS[config.slug],
            backgroundUrl: config.backgroundUrl,
          },
        ];
      }),
    [footballLeagueLaunches2027]
  );
};

const secondaryLeagueLabelMessages = defineMessages({
  contender: {
    defaultMessage: 'Contender',
  },
  restOfTheWorld: {
    defaultMessage: 'Rest of the World',
  },
});

export const useSecondaryLeagueLabel = (leagueLaunch: {
  launchType: LaunchType;
}): string | undefined => {
  const { formatMessage } = useIntl();
  const { marketHomeFilters } = useMarketHomeFilters();

  if (leagueLaunch.launchType !== 'SECONDARY_LEAGUE') {
    return undefined;
  }

  const { rarity } = marketHomeFilters;
  const isLowerRarity = rarity === Rarity.limited || rarity === Rarity.rare;
  return formatMessage(
    isLowerRarity
      ? secondaryLeagueLabelMessages.contender
      : secondaryLeagueLabelMessages.restOfTheWorld
  );
};

export const getLeagueAvatar = withFragments(
  (
    league: {
      name?: string;
      slug?: string;
      so5Competition?: getLeagueAvatar_so5Competition;
      competition?: getLeagueAvatar_competition;
    },
    mode: 'icon' | 'logo' = 'logo'
  ) => {
    const { so5Competition, competition } = league;
    const isSo5Competition = so5Competition?.competitions.length === 1;

    if (isSo5Competition) {
      return {
        name: so5Competition.displayName,
        slug: so5Competition.slug,
        avatarUrl:
          mode === 'logo' ? so5Competition.logoUrl : so5Competition.iconUrl,
      };
    }
    return {
      name: competition?.name || league.name || '',
      slug: competition?.slug || league.slug || '',
      avatarUrl: competition?.pictureUrl,
    };
  },
  {
    competition: gql`
      fragment getLeagueAvatar_competition on Competition {
        slug
        name
        pictureUrl
      }
    ` as TypedDocumentNode<getLeagueAvatar_competition>,
    so5Competition: gql`
      fragment getLeagueAvatar_so5Competition on So5Competition {
        slug
        displayName
        logoUrl
        iconUrl
        competitions {
          slug
        }
      }
    ` as TypedDocumentNode<getLeagueAvatar_so5Competition>,
  }
);

export const getLeagueBackgroundUrl = withFragments(
  (league: {
    so5Competition?: getLeagueBackgroundUrl_so5Competition;
    competition?: getLeagueBackgroundUrl_competition;
    backgroundUrl?: string;
  }) => {
    return (
      league.so5Competition?.headerBackgroundDesktopUrl ||
      league.competition?.backgroundPictureUrl ||
      league?.backgroundUrl
    );
  },
  {
    competition: gql`
      fragment getLeagueBackgroundUrl_competition on Competition {
        slug
        backgroundPictureUrl
      }
    ` as TypedDocumentNode<getLeagueBackgroundUrl_competition>,
    so5Competition: gql`
      fragment getLeagueBackgroundUrl_so5Competition on So5Competition {
        slug
        headerBackgroundDesktopUrl
        competitions {
          slug
        }
      }
    ` as TypedDocumentNode<getLeagueBackgroundUrl_so5Competition>,
  }
);
